import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Service for coordinating user preferences across the application
 * Acts as an intermediary between auth and timezone services to prevent circular dependencies
 */
@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  private userTimezoneSubject = new BehaviorSubject<string | null>(null);
  public readonly userTimezone$ = this.userTimezoneSubject.asObservable();

  constructor() {
    const storedTimezone = localStorage.getItem('user_timezone');
    if (storedTimezone) {
      this.updateTimezone(storedTimezone);
    } else {
      const systemTimezone = this.getSystemTimezone();
      this.updateTimezone(systemTimezone);
    }
  }

  /**
   * Gets the system/browser timezone
   * @returns The system timezone ID or UTC as fallback
   */
  private getSystemTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.warn(
        'Could not determine system timezone, falling back to UTC:',
        error
      );
      return 'UTC';
    }
  }

  /**
   * Validates if a timezone string is a valid IANA timezone identifier
   * @param timezone The timezone string to validate
   * @returns true if valid, false otherwise
   */
  private isValidIANATimezone(timezone: string): boolean {
    try {
      Intl.DateTimeFormat('en', { timeZone: timezone });
      return true;
    } catch (error) {
      console.warn(`Invalid IANA timezone: ${timezone}`, error);
      return false;
    }
  }

  /**
   * Updates the user's timezone preference
   * @param timezone The timezone ID to set
   */
  updateTimezone(timezone: string | null): void {
    let validTimezone = timezone;

    if (timezone) {
      if (!this.isValidIANATimezone(timezone)) {
        validTimezone = this.getSystemTimezone();
      }

      if (validTimezone) {
        localStorage.setItem('user_timezone', validTimezone);
      }
    } else {
      validTimezone = this.getSystemTimezone();
      localStorage.setItem('user_timezone', validTimezone);
    }

    this.userTimezoneSubject.next(validTimezone);
  }

  getTimezone(): string {
    const currentTimezone = this.userTimezoneSubject.getValue();

    if (currentTimezone && this.isValidIANATimezone(currentTimezone)) {
      return currentTimezone;
    }

    const storedTimezone = localStorage.getItem('user_timezone');
    if (storedTimezone && this.isValidIANATimezone(storedTimezone)) {
      this.userTimezoneSubject.next(storedTimezone);
      return storedTimezone;
    }

    const systemTimezone = this.getSystemTimezone();
    this.updateTimezone(systemTimezone);
    return systemTimezone;
  }
}
